<template>
    <div class="flex flex-col text-gray-800">
        <label
            class="mt-1 relative flex text-body hover:cursor-pointer"
            :class="alignTop ? 'items-start' : 'items-center'"
        >
            <span v-if="dot" class="text-red-pink absolute -top-2 -left-2"
                >•</span
            >
            <input
                :id="name"
                v-model="inputValue"
                type="checkbox"
                :name="name"
                :value="value"
                class="hidden"
            />
            <div :class="inputClasses">
                <div
                    v-if="isChecked"
                    class="flex justify-center items-center"
                    :class="[
                        checkedColor,
                        rounded
                            ? full
                                ? 'rounded-full'
                                : 'rounded'
                            : 'rounded-sm',
                        {
                            'h-full w-full': !rounded,
                            'h-8 w-8': xl,
                            'h-5 w-5': lg && !full,
                            'h-4 w-4': lg && full,
                            'h-1.5 w-1.5': sm,
                            'h-2.5 w-2.5': !lg && !sm,
                        },
                    ]"
                >
                    <CheckIcon
                        class="text-white"
                        :class="{
                            'h-3 w-3': !rounded,
                            'h-4 w-4': xl,
                            'h-3 w-3': lg && !full,
                            'h-2 w-2': lg && full,
                            'h-1 w-1': sm,
                            'h-1.5 w-1.5': !lg && !sm,
                        }"
                    />
                </div>
            </div>
            <span :class="[labelClasses, { 'font-semibold': isChecked }]">
                {{ label }}
            </span>
            <slot />
        </label>
        <div v-if="error || info" class="text-subtitle-2">
            <span v-if="error" class="text-alert">{{ error }}</span>
            <span v-if="info && !error" class="text-gray-700">
                {{ info }}
            </span>
        </div>
    </div>
</template>

<script>
import CheckIcon from "@/Components/Icons/CheckIcon.vue";
import { fieldProps, useFieldSetup } from "@/Mixins/FormFieldMixin";
import { twMerge } from "tailwind-merge";
import { computed, defineComponent } from "vue";

export default defineComponent({
    name: "CheckboxField",
    components: {
        CheckIcon,
    },
    props: {
        ...fieldProps,
        rounded: {
            type: Boolean,
            default: false,
        },
        full: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [String, Boolean],
            default: true,
        },
        labelClasses: {
            type: String,
            default: "",
        },
        inputClass: {
            type: String,
            default: "",
        },
        alignTop: {
            type: Boolean,
            default: false,
        },
        checkedColor: {
            type: String,
            default: "bg-gray-850",
        },
        dot: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const fieldSetup = useFieldSetup(props, emit);
        const inputClasses = computed(() => [
            {
                "h-8 w-8": props.xl,
                "h-6 w-6": props.lg,
                "h-3 w-3": props.sm,
                "h-4 w-4": !props.lg && !props.sm,
            },
            props.rounded
                ? props.full
                    ? "rounded-full"
                    : "rounded"
                : "rounded-sm",
            twMerge(
                "shadow-inner flex justify-center items-center mr-2 border border-gray-900 border-opacity-10 flex-shrink-0 bg-white",
                props.inputClass
            ),
        ]);

        const isChecked = computed(() =>
            Array.isArray(fieldSetup.inputValue.value)
                ? fieldSetup.inputValue.value.includes(props.value)
                : fieldSetup.inputValue.value === props.value
        );

        return {
            ...fieldSetup,
            isChecked,
            inputClasses,
        };
    },
});
</script>
